import { useGT } from 'gt-react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { AgentPageEditorDialog } from '@/pages/authenticated/settings/organization/components/cards/agent-pages/AgentPageEditorDialog';
import type { AgentPageDraft } from '@/pages/authenticated/settings/organization/components/cards/agent-pages/agentPageAdmin';
import { TaskPageEditorDialog } from '@/pages/authenticated/settings/organization/components/cards/task-pages/TaskPageEditorDialog';
import type { TaskPageDraft } from '@/pages/authenticated/settings/organization/components/cards/task-pages/taskPageAdmin';

export function TaskPageSidebarDialogs({
  draft,
  initialDraft,
  archiveName,
  archiveOpen,
  isSaving,
  isRemoving,
  onChange,
  onClose,
  onSave,
  onArchiveClose,
  onArchive,
}: {
  draft: TaskPageDraft | null;
  initialDraft: TaskPageDraft | null;
  archiveName: string;
  archiveOpen: boolean;
  isSaving: boolean;
  isRemoving: boolean;
  onChange: (next: TaskPageDraft) => void;
  onClose: () => void;
  onSave: () => void;
  onArchiveClose: () => void;
  onArchive: () => void;
}) {
  const gt = useGT();
  return (
    <>
      <TaskPageEditorDialog
        draft={draft}
        initialDraft={initialDraft}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}
        isSaving={isSaving}
      />
      <ConfirmDialog
        open={archiveOpen}
        onClose={onArchiveClose}
        onConfirm={onArchive}
        title={gt('Remove "{name}" from the sidebar?', {
          context: 'confirm dialog title for removing a task page',
          name: archiveName,
        })}
        message={gt(
          'The page disappears from the sidebar for everyone. Tasks of its type are unaffected.',
          { context: 'confirm dialog body for removing a task page' },
        )}
        confirmText={gt('Remove', {
          context: 'confirm button for removing a task page from the sidebar',
        })}
        loading={isRemoving}
        closeOnConfirm={false}
        dangerous
      />
    </>
  );
}

export function AgentPageSidebarDialogs({
  draft,
  initialDraft,
  archiveName,
  archiveOpen,
  isSaving,
  isRemoving,
  onChange,
  onClose,
  onSave,
  onArchiveClose,
  onArchive,
}: {
  draft: AgentPageDraft | null;
  initialDraft: AgentPageDraft | null;
  archiveName: string;
  archiveOpen: boolean;
  isSaving: boolean;
  isRemoving: boolean;
  onChange: (next: AgentPageDraft) => void;
  onClose: () => void;
  onSave: () => void;
  onArchiveClose: () => void;
  onArchive: () => void;
}) {
  const gt = useGT();
  return (
    <>
      <AgentPageEditorDialog
        draft={draft}
        initialDraft={initialDraft}
        onChange={onChange}
        onClose={onClose}
        onSave={onSave}
        isSaving={isSaving}
      />
      <ConfirmDialog
        open={archiveOpen}
        onClose={onArchiveClose}
        onConfirm={onArchive}
        title={gt('Remove "{name}" from the sidebar?', {
          context: 'confirm dialog title for removing an agent page',
          name: archiveName,
        })}
        message={gt(
          'The page disappears from the sidebar for everyone. The agent and its runs are unaffected.',
          { context: 'confirm dialog body for removing an agent page' },
        )}
        confirmText={gt('Remove', {
          context: 'confirm button for removing an agent page from the sidebar',
        })}
        loading={isRemoving}
        closeOnConfirm={false}
        dangerous
      />
    </>
  );
}
